import React, { useEffect } from 'react';
import { X, FileText, MapPin } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

interface MapExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MapExplainerModal: React.FC<MapExplainerModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="map-explainer-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="map-explainer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-explainer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="map-explainer-header">
          <h2 id="map-explainer-title" className="map-explainer-title">
            {t('mapExplainer.title')}
          </h2>
          <button
            type="button"
            className="map-explainer-icon-btn"
            onClick={onClose}
            aria-label={t('aria.close')}
          >
            <X size={24} strokeWidth={2} />
          </button>
        </header>

        <div className="map-explainer-body">
          <section className="map-explainer-section">
            <div className="map-explainer-section-head">
              <FileText className="map-explainer-section-icon" size={22} aria-hidden />
              <h3 className="map-explainer-section-title">{t('mapExplainer.algorithmTitle')}</h3>
            </div>
            <p className="map-explainer-paragraph">{t('mapExplainer.algorithmBody')}</p>
          </section>

          <section className="map-explainer-section">
            <div className="map-explainer-section-head">
              <MapPin className="map-explainer-section-icon" size={22} aria-hidden />
              <h3 className="map-explainer-section-title">{t('mapExplainer.dataTitle')}</h3>
            </div>
            <p className="map-explainer-paragraph">{t('mapExplainer.dataBody1')}</p>
            <p className="map-explainer-paragraph">{t('mapExplainer.dataBody2')}</p>
          </section>

          <section className="map-explainer-section">
            <p className="map-explainer-paragraph map-explainer-limitations-intro">
              {t('mapExplainer.limitationsIntro')}
            </p>
            <ul className="map-explainer-list">
              <li>
                <strong>{t('mapExplainer.limitationOutdatedLabel')}</strong>{' '}
                {t('mapExplainer.limitationOutdatedBody')}
              </li>
              <li>
                <strong>{t('mapExplainer.limitationWorksLabel')}</strong>{' '}
                {t('mapExplainer.limitationWorksBody')}
              </li>
            </ul>
          </section>
        </div>

        <footer className="map-explainer-footer">
          <button type="button" className="map-explainer-footer-btn" onClick={onClose}>
            {t('mapExplainer.close')}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default MapExplainerModal;
